"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { IconArrowNarrowLeft, IconArrowNarrowRight } from "@tabler/icons-react";

import { Button } from "@/components/ui/button";
import { WobbleCard } from "@/components/ui/wobble-card";
import { useServiceMenuItems } from "@/hooks/useServiceMenuItems";
import { Link } from "@/i18n/navigation";
import { getServicePath } from "@/lib/service-slug";

type InstallationTypesSectionProps = {
  title?: string;
};

export const InstallationTypesSection = ({ title }: InstallationTypesSectionProps) => {
  const t = useTranslations("home");
  const products = useServiceMenuItems();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const getScrollStep = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return 360;
    const card = container.querySelector<HTMLElement>(":scope > *");
    if (!card) return 360;
    return card.offsetWidth + 24;
  }, []);

  const checkScrollability = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  const handleScrollLeft = () => {
    const container = scrollRef.current;
    if (!container) return;
    const newScroll = Math.max(container.scrollLeft - getScrollStep(), 0);
    container.scrollTo({ left: newScroll, behavior: "smooth" });
  };

  const handleScrollRight = () => {
    const container = scrollRef.current;
    if (!container) return;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const newScroll = Math.min(container.scrollLeft + getScrollStep(), maxScroll);
    container.scrollTo({ left: newScroll, behavior: "smooth" });
  };

  useEffect(() => {
    checkScrollability();

    window.addEventListener("resize", checkScrollability);

    return () => {
      window.removeEventListener("resize", checkScrollability);
    };
  }, [products.length, checkScrollability]);
  return (
    <section className="bg-surface-light px-4 section-y dark:bg-surface-dark-raised">
      <div className="mx-auto max-w-6xl space-y-8">
        <div className="text-center">
          <h2 className="text-brand-navy dark:text-white type-h2">
            {title ?? t("installationTypes.title")}
          </h2>
          <p className="mt-3 text-gray-700 dark:text-gray-300">
            {t("installationTypes.description")}
          </p>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={handleScrollLeft}
            disabled={!canScrollLeft}
            aria-label={t("testimonials.scrollLeft")}
            className="absolute left-5 top-1/2 z-10 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-lg transition-all hover:bg-white disabled:cursor-not-allowed disabled:opacity-40 sm:h-10 sm:w-10"
          >
            <IconArrowNarrowLeft className="h-5 w-5 text-gray-700 sm:h-6 sm:w-6" />
          </button>
          <button
            type="button"
            onClick={handleScrollRight}
            disabled={!canScrollRight}
            aria-label={t("testimonials.scrollRight")}
            className="absolute right-5 top-1/2 z-10 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-lg transition-all hover:bg-white disabled:cursor-not-allowed disabled:opacity-40 sm:h-10 sm:w-10"
          >
            <IconArrowNarrowRight className="h-5 w-5 text-gray-700 sm:h-6 sm:w-6" />
          </button>
          <div
            ref={scrollRef}
            onScroll={checkScrollability}
            className="flex items-stretch snap-x snap-mandatory gap-6 overflow-x-auto pb-4 [-webkit-overflow-scrolling:touch]"
          >
            {products
              .filter(
                (item) =>
                  item.slug.toLowerCase() !== "casos-de-exito")
              .map((item, index) => (
              <Link
                key={item.slug}
                href={getServicePath(item.slug)}
                className="flex w-[280px] min-w-[280px] shrink-0 snap-start self-stretch sm:w-[320px] sm:min-w-[320px] lg:w-[360px] lg:min-w-[360px]"
              >
                <WobbleCard
                  containerClassName="group flex h-full w-full bg-white dark:bg-surface-dark border border-white/70 dark:border-white/10"
                  className="h-full px-7 py-9"
                >
                  <div className="flex h-full min-h-[420px] flex-col">
                    <div className="relative mb-5 h-44 w-full overflow-hidden rounded-card">
                      <Image
                        src={item.image}
                        alt={`instalaciones fotovoltaicas - ${item.title}`}
                        fill
                        className="object-cover transition-transform duration-300 group-hover:scale-105"
                        sizes="(min-width: 1024px) 25vw, 50vw"
                        quality={70}
                        priority={index === 0}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-80" />
                    </div>
                    <h4 className="text-xl font-semibold text-brand-navy dark:text-white">
                      {item.title}
                    </h4>
                    <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                      {item.description}
                    </p>
                    <div className="mt-auto pt-5">
                      <Button
                        className="w-full rounded-full bg-brand-navy px-4 py-3 text-sm font-semibold uppercase text-white shadow-card transition-transform duration-200 hover:-translate-y-0.5 hover:bg-brand-navy-dark dark:bg-gold dark:text-surface-dark dark:hover:bg-gold-dark"
                        aria-label={`${t("installationTypes.cta")}: ${item.title}`}
                      >
                        {t("installationTypes.cta")}
                      </Button>
                    </div>
                  </div>
                </WobbleCard>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
